import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { inr } from "@/components/common";
import Seo from "@/components/Seo";

export default function Pricing() {
  const navigate = useNavigate();
  const [data, setData] = useState({ plans: [], commission_pct: 5 });
  useEffect(() => { api.get("/pricing").then((r) => setData(r.data)).catch(() => {}); }, []);

  return (
    <div>
      <Seo
        title="Agency Subscription Plans & Pricing | GuardDekho.com"
        description="Transparent subscription plans for security agencies on GuardDekho.com. Get more leads, featured listings and management tools to grow your security business in India."
      />
      <section className="bg-navy-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-16 text-center">
          <span className="brand-label text-brand">Agency Subscriptions</span>
          <h1 className="mt-3 font-heading text-4xl sm:text-5xl font-extrabold tracking-tighter">Plans that scale with your agency</h1>
          <p className="mt-4 text-navy-300 max-w-2xl mx-auto text-lg">Grow your security business with more leads, featured listings and management tools.</p>
        </div>
      </section>
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-3 gap-6 items-start">
          {data.plans.map((p, i) => {
            const featured = !!p.highlight;
            const cur = p.currency === "INR" || !p.currency ? "₹" : (p.currency + " ");
            const period = p.billing_period && p.billing_period !== "one-time" ? `/${p.billing_period.replace("ly", "")}` : "";
            return (
              <div key={p.code || p.name} data-testid={`plan-${(p.code || p.name).toLowerCase()}`}
                className={`rounded-2xl border p-8 ${featured ? "border-brand bg-white ring-2 ring-brand shadow-xl relative" : "border-navy-200 bg-white"}`}>
                {featured && <span className="absolute -top-3 left-8 bg-brand text-white text-xs font-semibold px-3 py-1 rounded-full">Most Popular</span>}
                <h3 className="font-heading text-xl font-bold text-navy-900">{p.name}</h3>
                {p.description && <p className="mt-1 text-sm text-navy-500">{p.description}</p>}
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-heading text-4xl font-extrabold text-navy-900">{p.price === 0 ? `${cur}0` : (p.currency && p.currency !== "INR" ? cur + Number(p.price).toLocaleString() : inr(p.price))}</span>
                  <span className="text-navy-500">{period}</span>
                </div>
                <ul className="mt-6 space-y-3">
                  {(p.features || []).map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-navy-700"><Check size={16} className="text-emerald-500 mt-0.5 shrink-0" /> {f}</li>
                  ))}
                </ul>
                <Button className={`mt-8 w-full h-11 ${featured ? "bg-brand hover:bg-brand-600" : "bg-navy-900 hover:bg-navy-800"}`} onClick={() => navigate("/for-agencies")}>
                  Get started <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            );
          })}
        </div>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {[["Commission", `${data.commission_pct}% of contract value`, "Charged on successfully facilitated contracts."],
            ["Lead Fees", "Pay-per-qualified-lead", "Optional lead purchases beyond your plan quota."],
            ["Featured Placement", "Premium visibility", "Boost your agency to the top of search results."]].map(([t, v, d]) => (
            <div key={t} className="bg-navy-50 border border-navy-200 rounded-xl p-6">
              <div className="brand-label text-brand">{t}</div>
              <div className="mt-2 font-heading text-lg font-bold text-navy-900">{v}</div>
              <p className="mt-1 text-sm text-navy-500">{d}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
